import { type QueryKey, skipToken, type UseQueryResult, useQuery } from '@tanstack/react-query';

// Overloaded so a selected result type can only be requested by actually passing `select`.
// A single signature with an optional `select` would let callers name a TSelected that the
// runtime value never becomes.
export function usePublishedSnapshotQuery<TData>(args: {
  baseKey: QueryKey;
  getSnapshot: () => TData | undefined;
}): UseQueryResult<TData | undefined, Error>;
export function usePublishedSnapshotQuery<TData, TSelected>(args: {
  baseKey: QueryKey;
  getSnapshot: () => TData | undefined;
  select: (data: TData | undefined) => TSelected;
}): UseQueryResult<TSelected, Error>;
// A published view ships its lookups up front, so there is nothing to fetch. This parks that
// snapshot in the query cache under `<baseKey, 'published'>` so callers can hand consumers a
// normal UseQueryResult instead of a second, context-shaped return type.
export function usePublishedSnapshotQuery<TData, TSelected>({
  baseKey,
  getSnapshot,
  select,
}: {
  baseKey: QueryKey;
  getSnapshot: () => TData | undefined;
  select?: (data: TData | undefined) => TSelected;
}) {
  return useQuery<TData | undefined, Error, TSelected>({
    queryKey: [...baseKey, 'published'],
    queryFn: skipToken,
    initialData: getSnapshot,
    select,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
